import { useState, useRef, useEffect } from "react";
import { activate } from "../../../../utils/keyboard-activate";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import type { DragEndEvent, Modifier } from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { TeamResponse } from "../../../../types/api";
import { TeamEditorModal } from "./TeamEditorModal";
import { TrashIcon } from "../../../../assets/TrashIcon";
import { tourAnchor } from "../../../tours/tourAnchors";
import "./TeamChips.css";

interface TeamChipsProps {
  teams: TeamResponse[]; // All available teams
  selectedTeamId: string | null; // Currently active team
  teamChips: TeamResponse[]; // Teams to display as chips
  onChange: (teamId: string | null) => void;
  onAddTeam: (team: TeamResponse) => void;
  onRemoveTeam: (teamId: string) => void;
  /** Close every team chip at once and return to All Teams. */
  onCloseAllTeams: () => void;
  onDeleteTeam: (teamId: string) => void;
  onTeamUpdate: () => void;
  onReorderTeams: (newOrder: TeamResponse[]) => void;
  /** Opens the bulk manage-teams panel from the Add Team modal header. */
  onManageTeams?: () => void;
  /**
   * Teams that read as JUCO, rolled up from the loaded prospects by
   * `utils/juco-team-rollup` (a majority of the players whose level is known).
   *
   * Optional and defaulted to empty: a caller with no prospects loaded gets
   * exactly the chip row it rendered before the marker existed, rather than a
   * required prop threaded through every call site for a decorative hint.
   */
  jucoTeamIds?: ReadonlySet<string>;
}

interface SortableTeamChipProps {
  team: TeamResponse;
  selectedTeamId: string | null;
  isJuco: boolean;
  onChipClick: (teamId: string) => void;
  onMenuClick: (teamId: string) => void;
}

const SortableTeamChip = ({
  team,
  selectedTeamId,
  isJuco,
  onChipClick,
  onMenuClick,
}: SortableTeamChipProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: team.id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: 1,
    cursor: isDragging ? "grabbing" : "grab",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`team-chip ${
        selectedTeamId === team.id ? "team-chip--active" : ""
      }`}
      {...attributes}
      {...listeners}
    >
      <span className="team-chip__name" {...activate(() => onChipClick(team.id))}>
        {team.name}
      </span>
      {/* The marker is a word, not a colour, so it is announced as-is and needs
          no extra label. `title` spells the abbreviation out on hover. */}
      {isJuco && (
        <span className="team-chip__badge" title="Junior college team">
          JUCO
        </span>
      )}
      {selectedTeamId === team.id && (
        <button
          className="team-chip__menu-button"
          onClick={(e) => {
            e.stopPropagation();
            onMenuClick(team.id);
          }}
          aria-label="Team options"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="3" cy="8" r="1.5" />
            <circle cx="8" cy="8" r="1.5" />
            <circle cx="13" cy="8" r="1.5" />
          </svg>
        </button>
      )}
    </div>
  );
};

/** Module-level so the default prop is referentially stable across renders. */
const EMPTY_TEAM_IDS: ReadonlySet<string> = new Set<string>();

export const TeamChips = ({
  teams,
  selectedTeamId,
  teamChips,
  onChange,
  onAddTeam,
  onRemoveTeam,
  onCloseAllTeams,
  onDeleteTeam,
  onTeamUpdate,
  onReorderTeams,
  onManageTeams,
  jucoTeamIds = EMPTY_TEAM_IDS,
}: TeamChipsProps) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [removeTeamId, setRemoveTeamId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  // Teams the user has ticked in the Add modal, in click order. Lets them pick
  // several at once and open them all as tabs with a single "Add" instead of
  // reopening the picker per team.
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const modalRef = useRef<HTMLDivElement>(null);
  const chipsContainerRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    // Without this, reordering tabs is mouse-only (WCAG 2.1.1). Matches how
    // PlayersTable configures its own sortable list.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const restrictToHorizontalWithinContainer: Modifier = ({ transform, draggingNodeRect }) => {
    if (!chipsContainerRef.current || !draggingNodeRect) {
      return { ...transform, y: 0 };
    }
    const containerRect = chipsContainerRef.current.getBoundingClientRect();
    const minX = containerRect.left - draggingNodeRect.left;
    const maxX = containerRect.right - draggingNodeRect.right;
    return {
      ...transform,
      x: Math.min(Math.max(transform.x, minX), maxX),
      y: 0,
    };
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = teamChips.findIndex((t) => t.id === active.id);
    const newIndex = teamChips.findIndex((t) => t.id === over.id);
    onReorderTeams(arrayMove(teamChips, oldIndex, newIndex));
  };

  // Reset transient modal state (search + pending selection) and close it.
  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setSearchQuery("");
    setSelectedTeamIds([]);
  };

  // Close add modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        closeAddModal();
      }
    };

    if (isAddModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isAddModalOpen]);

  const handleChipClick = (teamId: string | null) => {
    onChange(teamId);
  };

  // Toggle a team in/out of the pending multi-select.
  const handleToggleTeam = (teamId: string) => {
    setSelectedTeamIds((prev) =>
      prev.includes(teamId)
        ? prev.filter((id) => id !== teamId)
        : [...prev, teamId]
    );
  };

  // Add every ticked team as a chip at once, then activate the first one.
  // Skip any team that's already a chip so a stale tick can't push a duplicate
  // (duplicate chip ids break the sortable list's keys and drag-reorder).
  const handleAddSelectedTeams = () => {
    if (selectedTeamIds.length === 0) return;
    const teamsToAdd = selectedTeamIds
      .map((id) => teams.find((t) => t.id === id))
      .filter((t): t is TeamResponse => !!t)
      .filter((t) => !teamChips.some((chip) => chip.id === t.id));
    teamsToAdd.forEach((team) => onAddTeam(team));
    if (teamsToAdd.length > 0) {
      onChange(teamsToAdd[0].id);
    }
    closeAddModal();
  };

  // Get teams that are not already in the chip list
  const availableTeams = teams.filter(
    (team) => !teamChips.find((chip) => chip.id === team.id)
  );

  // Filter available teams by search query
  const filteredTeams = searchQuery
    ? availableTeams.filter((team) =>
        team.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : availableTeams;

  // Get the team to be removed
  const teamToRemove = teamChips.find((team) => team.id === removeTeamId);

  return (
    <div className="team-chips" {...tourAnchor("players.teams")}>
      <div className="team-chips__container" ref={chipsContainerRef}>
        {/* Add team button */}
        <button
          className="team-chips__add-button"
          onClick={() => setIsAddModalOpen(true)}
          aria-label="Add team"
          {...tourAnchor("players.teams.add")}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M8 3v10M3 8h10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* All Teams chip - always shown */}
        <div
          className={`team-chip ${
            selectedTeamId === null ? "team-chip--active" : ""
          }`}
          {...activate(() => handleChipClick(null))}
        >
          <span className="team-chip__name">All Teams</span>
        </div>

        {/* Favorites star chip — cross-team favorites filter */}
        <div
          className={`team-chip team-chip--icon-only ${
            selectedTeamId === "favorites" ? "team-chip--active" : ""
          }`}
          {...activate(() => handleChipClick("favorites"))}
          role="tab"
          aria-label="Favorites"
          aria-selected={selectedTeamId === "favorites"}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill={selectedTeamId === "favorites" ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </div>

        {/* User-selected team chips */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToHorizontalWithinContainer]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={teamChips.map((t) => t.id)}
            strategy={horizontalListSortingStrategy}
          >
            {teamChips.map((team) => (
              <SortableTeamChip
                key={team.id}
                team={team}
                selectedTeamId={selectedTeamId}
                isJuco={jucoTeamIds.has(team.id)}
                onChipClick={handleChipClick}
                onMenuClick={setRemoveTeamId}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      {/* Add Team Modal */}
      {isAddModalOpen && (
        <div className="team-chips__modal-overlay">
          <div className="team-chips__modal" ref={modalRef}>
            <div className="team-chips__modal-header">
              <h3>Add Team</h3>
              <div className="team-chips__modal-header-actions">
                {onManageTeams && (
                  <button
                    type="button"
                    className="team-chips__modal-manage"
                    title="Manage teams"
                    aria-label="Manage teams"
                    onClick={() => {
                      closeAddModal();
                      onManageTeams();
                    }}
                  >
                    <TrashIcon />
                  </button>
                )}
                <button
                  className="team-chips__modal-close"
                  onClick={closeAddModal}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="team-chips__modal-search">
              <input
                type="text"
                placeholder="Search teams..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
            <div className="team-chips__modal-content">
              {availableTeams.length === 0 ? (
                <div className="team-chips__modal-empty">
                  No more teams available
                </div>
              ) : filteredTeams.length > 0 ? (
                filteredTeams.map((team) => {
                  const isSelected = selectedTeamIds.includes(team.id);
                  return (
                    <div
                      key={team.id}
                      className={`team-chips__modal-option ${
                        isSelected ? "team-chips__modal-option--selected" : ""
                      }`}
                      onClick={() => handleToggleTeam(team.id)}
                      onKeyDown={(e) => {
                        if (e.key === " " || e.key === "Enter") {
                          e.preventDefault();
                          handleToggleTeam(team.id);
                        }
                      }}
                      role="checkbox"
                      aria-checked={isSelected}
                      tabIndex={0}
                    >
                      <span className="team-chips__modal-option-name">
                        {team.name}
                      </span>
                      <span
                        className={`team-chips__modal-checkbox ${
                          isSelected ? "team-chips__modal-checkbox--checked" : ""
                        }`}
                        aria-hidden="true"
                      >
                        {isSelected && (
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M13 4L6 11L3 8"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="team-chips__modal-empty">No teams found</div>
              )}
            </div>
            {selectedTeamIds.length > 0 && (
              <div className="team-chips__modal-footer">
                <button
                  type="button"
                  className="team-chips__modal-add-button"
                  onClick={handleAddSelectedTeams}
                >
                  {`Add ${selectedTeamIds.length} ${
                    selectedTeamIds.length === 1 ? "Team" : "Teams"
                  }`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Remove Team Modal */}
      {teamToRemove && (
        <TeamEditorModal
          team={teamToRemove}
          isOpen={!!removeTeamId}
          onClose={() => setRemoveTeamId(null)}
          onRemove={onRemoveTeam}
          onCloseAll={onCloseAllTeams}
          onDelete={onDeleteTeam}
          onTeamUpdate={onTeamUpdate}
        />
      )}
    </div>
  );
};
